import { FC, useState, useEffect } from 'react'
import classnames from 'classnames'
import { QuizAnswerContentContainer as Container } from 'mytours-read-common'
import { DexieQuestion } from 'mobile_web/modules/db/types'
import { MediaItem, Question } from 'mytours-read-api-typescript'
import { saveResultToDB } from '../queries'
import db from 'mobile_web/modules/db'
import { useLiveQuery } from 'dexie-react-hooks'
import { IonGrid } from '@ionic/react'
import { InputChangeEventDetail } from '@ionic/core'
import TextChoiceAnswer from './TextChoiceAnswer'
import ImageChoiceAnswer from './ImageChoiceAnswer'
import FreeTextAnswer from './FreeTextAnswer'
import Button from '../../common/components/Button'
import AnswerModal from './AnswerModal'
import strings from 'mobile_web/strings'
import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageUrl, BaseUrlEnum, generateFilePath } from '../../../common_lib/utils'

interface QuizAnswersContentProps {
	quizId: number
	question: DexieQuestion
}

const QuizAnswersContent: FC<QuizAnswersContentProps & Container.Actions> = ({
	question,
	quizId,
	toggleBadgeModal,
}) => {
	const [isAnswered, setIsAnswered] = useState(false)
	const [checkedAnswers, setCheckedAnswers] = useState<number[]>([])
	const [textAnswer, setTextAnswer] = useState('')
	const [attemptsUsed, setAttemptsUsed] = useState(0)
	const [answerModalOpen, setAnswerModalOpen] = useState(false)
	const [answeredCorrect, setAnsweredCorrect] = useState(false)
	const [shouldShowBadgeModal, setShowBadgeModal] = useState(false)

	const earnedBadges = useLiveQuery(() => db.recentlyEarnedBadges.toArray())
	const trigger = useLiveQuery(
		() => question && db.triggers.where({ triggerable_id: question?.id, trigger_type: 'quiz_question' }).first(),
		[question],
	)

	useEffect(() => {
		if (question && trigger && earnedBadges) {
			const earned = !!earnedBadges.find(b => b.badge_id === trigger.badge_id)
			if (!earned) {
				setShowBadgeModal(true)
			}
		}
	}, [question, trigger, earnedBadges])

	const questionType = question?.question_type

	const isImage =
		questionType === Question.QuestionTypeEnum.SingleImageChoice ||
		questionType === Question.QuestionTypeEnum.MultiImageChoice
	const isMulti =
		questionType === Question.QuestionTypeEnum.MultiImageChoice ||
		questionType === Question.QuestionTypeEnum.MultiTextChoice
	const isFreeText = questionType === Question.QuestionTypeEnum.FreeText

	useEffect(() => {
		if (question) {
			;(async () => {
				const results = await db.quizResults.get({ question_id: question.id })
				const setAnswers = (answers: number[] | string) =>
					isFreeText ? setTextAnswer(answers as string) : setCheckedAnswers([...(answers as number[])])
				if (results) {
					setAnswers(results.answers)
					setIsAnswered(true)
					setAttemptsUsed(results.attempts_used)
				}
			})()
		}
	}, [question])

	const checkCorrect = (checkedAnswers: number[] | string): boolean => {
		const { answers } = question
		if (isFreeText) {
			return answers?.some(answer => answer.answer_text.toLowerCase() === textAnswer.toLowerCase())
		}

		if (!isMulti && !isFreeText) {
			return (checkedAnswers as number[]).some(id => answers.find(a => a.id === id).correct)
		}

		const isCorrect = (checkedAnswers as number[]).every(id => answers.find(a => a.id === id).correct)
		return isCorrect && answers.filter(a => a.correct).length === checkedAnswers.length
	}

	const handleSubmitAnswer = (answers: number[] | string): void => {
		setIsAnswered(true)
		setAttemptsUsed(attemptsUsed + 1)
		const isAnsweredCorrect = checkCorrect(isMulti ? checkedAnswers : answers)
		setAnsweredCorrect(isAnsweredCorrect)
		if (shouldShowBadgeModal && isAnsweredCorrect) {
			toggleBadgeModal(true, trigger.badge_id, true, true)
		}
		setAnswerModalOpen(true)
		saveResultToDB(question.id, quizId, isMulti ? checkedAnswers : answers, isAnsweredCorrect, attemptsUsed + 1)
	}

	const handleClearCurrentAnswers = () => {
		setIsAnswered(false)
		setCheckedAnswers([])
		setTextAnswer('')
	}

	const handleClickAnswer = (id: number): void => {
		if (checkedAnswers.includes(id)) {
			setCheckedAnswers([...checkedAnswers.filter(answerId => answerId !== id)])
		} else {
			setCheckedAnswers([...checkedAnswers, id])
		}
		if (!isMulti) {
			handleSubmitAnswer([...checkedAnswers, id])
		}
	}

	const getAudioFilePath = (mediaItem: MediaItem): string => {
		if (!mediaItem) {
			return
		}

		const { file, dont_transform: dontTransform } = mediaItem
		const { filename, id, region } = file

		const { base, path } = generateFilePath(String(id), BaseUrlEnum.BunnyUploads, {
			pathType: dontTransform ? 'original' : 'quality_7',
			region,
		})

		return base + path + filename
	}

	const hasAttempts = attemptsUsed < question?.max_attempts
	const hasAttemptsAndAnswered = hasAttempts && isAnswered

	const imageMediaItem = answeredCorrect ? question?.correct_image_media_item : question?.incorrect_image_media_item
	const imageSrc = imageMediaItem?.file && generateImageUrl(imageMediaItem.file, { imageSizePreset: ImageSizePreset.Row })

	const audioMediaItem = answeredCorrect ? question?.correct_audio_media_item : question?.incorrect_audio_media_item

	return (
		<>
			<AnswerModal
				isOpen={answerModalOpen}
				onClose={() => setAnswerModalOpen(false)}
				isCorrect={answeredCorrect}
				text={answeredCorrect ? question?.correct_text : question?.incorrect_text}
				imageSrc={imageSrc}
				audioSrc={getAudioFilePath(audioMediaItem)}
			/>
			<IonGrid className={classnames({ 'image-grid': isImage })}>
				{!isFreeText &&
					question?.answers?.map(answer =>
						isImage ? (
							<ImageChoiceAnswer
								key={answer.id}
								onClick={!isAnswered ? () => handleClickAnswer(answer.id) : undefined}
								answer={answer}
								isAnswered={isAnswered}
								isChecked={checkedAnswers.includes(answer.id)}
								hasAttempts={hasAttempts}
							/>
						) : (
							<TextChoiceAnswer
								key={answer.id}
								onClick={!isAnswered ? () => handleClickAnswer(answer.id) : undefined}
								answer={answer}
								isAnswered={isAnswered}
								isChecked={checkedAnswers.includes(answer.id)}
								hasAttempts={hasAttempts}
							/>
						),
					)}
				{isFreeText && (
					<FreeTextAnswer
						isAnswered={isAnswered}
						answers={question?.answers}
						onSubmit={hasAttemptsAndAnswered ? () => handleClearCurrentAnswers() : () => handleSubmitAnswer(textAnswer)}
						textAnswer={textAnswer}
						onChange={(e: CustomEvent<InputChangeEventDetail>) => setTextAnswer((e.target as HTMLInputElement).value)}
						hasAttempts={hasAttempts}
					/>
				)}
			</IonGrid>
			{(isMulti || (hasAttemptsAndAnswered && !isFreeText)) && !answeredCorrect && (
				<Button
					fill="clear"
					onClick={hasAttemptsAndAnswered ? handleClearCurrentAnswers : handleSubmitAnswer}
					ariaLabel={strings.submit}
					disabled={isAnswered && !hasAttempts}
				>
					{hasAttemptsAndAnswered ? strings.try_again : strings.submit}
				</Button>
			)}
		</>
	)
}

const QuizAnswersContentComponent = Container.default(QuizAnswersContent)
export default QuizAnswersContentComponent as any
